import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  cancelCoachSession,
  completeCoachSession,
  getCoachConfig,
  listCoachSessions,
  patchCoachSession,
  startCoachSession,
  AssessmentId,
} from "../../../services/rest/kairaAssessments";
import {
  CareTriadKey,
  AssessmentMode,
  useAssessmentProgress,
  VIEW_TO_ASSESSMENT,
} from "./AssessmentProgressContext";
import {
  assessmentQuestions,
  isAnswered,
  questionIsVisible,
} from "../SharedProfile/showIf";
import useCoachPermissions from "../../../hooks/useCoachPermissions";

export const enum ConfirmKind {
  BaselineBlock = "baseline-block",
  FollowupWarn = "followup-warn",
}

/**
 * Session answers may be stored nested under a section id. Lift those onto
 * question ids so the page can count and render them against the config.
 */
const flattenCoachAnswers = (
  answers: Record<string, unknown> | null | undefined,
  config: any,
): Record<string, unknown> => {
  if (!answers || typeof answers !== "object" || Array.isArray(answers)) {
    return {};
  }
  const ids = new Set(
    (config?.questions || []).map((q: any) => q?.id).filter(Boolean),
  );
  const out: Record<string, unknown> = { ...answers };
  for (const [key, value] of Object.entries(answers)) {
    if (ids.has(key)) continue;
    if (!value || typeof value !== "object" || Array.isArray(value)) continue;
    const nested = value as Record<string, unknown>;
    if (nested.skipped === true) continue;
    for (const [innerKey, innerVal] of Object.entries(nested)) {
      if (!ids.has(innerKey)) continue;
      if (out[innerKey] == null || !isAnswered(out[innerKey])) {
        out[innerKey] = innerVal;
      }
    }
  }
  return out;
};

/** Answered count / pct from intersection of answers with current config questions. */
const progressFromConfig = (
  config: any,
  answers: Record<string, unknown>,
) => {
  const questions: any[] = assessmentQuestions(config).filter((q: any) =>
    questionIsVisible(q, answers),
  );
  const total = questions.length;
  let answered = 0;
  questions.forEach((q) => {
    if (q?.id != null && isAnswered(answers[q.id])) answered += 1;
  });
  const requiredQs = questions.filter((q) => q?.required === true);
  const toCheck = requiredQs.length ? requiredQs : questions;
  let requiredAnswered = 0;
  toCheck.forEach((q) => {
    if (q?.id != null && isAnswered(answers[q.id])) requiredAnswered += 1;
  });
  const requiredTotal = toCheck.length;
  const pct = total ? Math.min(100, Math.round((answered / total) * 100)) : 0;
  return { answered, total, pct, requiredAnswered, requiredTotal };
};

const sessionKey = (s: any) => String(s?.sessionId || s?.id || "");

const sessionCount = (s: any): number | null =>
  typeof s?.assessmentCount === "number" && Number.isFinite(s.assessmentCount)
    ? s.assessmentCount
    : null;

const visitCount = (s: any) =>
  typeof s?.assessmentCount === "number" ? s.assessmentCount : -1;

/** Newest visit owns the overview and sidebar bars. Baseline only when nothing later exists. */
const isLatestVisit = (row: any, rows: any[]) => {
  const count = visitCount(row);
  if (count < 0) return false;
  const max = rows.reduce((best, s) => Math.max(best, visitCount(s)), -1);
  return count === max;
};

const matchesMode = (s: any, mode: AssessmentMode) => {
  const n = sessionCount(s);
  if (mode === AssessmentMode.Baseline) return n === 0;
  return n != null && n >= 1;
};

/**
 * Load, save, and publish one Care Triad assessment for the current patient.
 * Baseline vs follow-up is `mode`; edit rights come from `canEditAssessment`.
 */
export const useCoachAssessment = (
  view: CareTriadKey,
  patientId: string | undefined,
  mode: AssessmentMode = AssessmentMode.Followup,
) => {
  const assessmentId: AssessmentId = VIEW_TO_ASSESSMENT[view];
  const { setPct, refreshAll, registerChrome } = useAssessmentProgress();
  const { canEditAssessment } = useCoachPermissions();

  const [config, setConfig] = useState<any>(null);
  const [sessions, setSessions] = useState<any[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveNote, setSaveNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [skipBaseline, setSkipBaseline] = useState(false);
  const [confirmKind, setConfirmKind] = useState<ConfirmKind | null>(null);
  const [dirty, setDirty] = useState(false);
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const configRef = useRef(config);
  configRef.current = config;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const loadGenRef = useRef(0);
  const selectGenRef = useRef(0);
  const persistTailRef = useRef(Promise.resolve(true));

  const activeSession = useMemo(
    () => sessions.find((s) => sessionKey(s) === activeSessionId) || null,
    [sessions, activeSessionId],
  );

  const action = activeSession?.action || "draft";
  const isPublished = action === "published";
  const isPreview = action === "preview";
  const source = activeSession?.source || "patient";
  const roleCanEdit = canEditAssessment(view);
  const canEdit = roleCanEdit && !isPublished;
  const pickerSessions = useMemo(
    () => sessions.filter((s) => matchesMode(s, mode)),
    [mode, sessions],
  );
  const baselinePublished = useMemo(
    () =>
      sessions.some(
        (s) => sessionCount(s) === 0 && (s.action || "") === "published",
      ),
    [sessions],
  );
  const coachQuestions = useMemo(() => {
    const raw = activeSession?.coachQuestions;
    if (!Array.isArray(raw)) return [] as string[];
    return raw
      .map((q: unknown) => (typeof q === "string" ? q.trim() : ""))
      .filter(Boolean);
  }, [activeSession]);
  const occurrence =
    activeSession?.occurrence ||
    activeSession?.label ||
    (typeof activeSession?.assessmentCount === "number"
      ? activeSession.assessmentCount === 0
        ? "Baseline"
        : `Follow-up ${activeSession.assessmentCount}`
      : null);
  const assessmentCount =
    typeof activeSession?.assessmentCount === "number"
      ? activeSession.assessmentCount
      : null;

  const applyProgress = useCallback(
    (cfg: any, nextAnswers: Record<string, unknown>, sessionAction?: string) => {
      const { pct } = progressFromConfig(cfg, nextAnswers);
      const shown = sessionAction === "published" ? 100 : pct;
      setPct(view, shown);
      return shown;
    },
    [setPct, view],
  );

  /** Config for one session (sessionId + count) so baseline vs follow-up seeds stay distinct. */
  const fetchConfigForSession = useCallback(
    async (row: any | null) => {
      if (!patientId) return null;
      const sid = row ? sessionKey(row) : null;
      const count =
        typeof row?.assessmentCount === "number" ? row.assessmentCount : null;
      return getCoachConfig(assessmentId, patientId, {
        sessionId: sid || undefined,
        assessmentCount: count,
      });
    },
    [assessmentId, patientId],
  );

  const load = useCallback(async () => {
    if (!patientId) return;
    const gen = ++loadGenRef.current;
    setLoading(true);
    setError(null);
    try {
      const list = await listCoachSessions(patientId, assessmentId);
      if (gen !== loadGenRef.current) return;
      const rows = list?.sessions || [];
      setSessions(rows);
      setSkipBaseline(Boolean(list?.skipBaseline));
      const visible = rows.filter((s: any) => matchesMode(s, mode));
      const preferred =
        visible.find((s: any) => s.action === "draft" || s.action === "preview") ||
        visible[visible.length - 1] ||
        null;

      const cfg = await fetchConfigForSession(preferred);
      if (gen !== loadGenRef.current) return;
      setConfig(cfg);

      if (preferred) {
        const id = sessionKey(preferred);
        const nextAnswers = flattenCoachAnswers(preferred.answers || {}, cfg);
        setActiveSessionId(id);
        setAnswers(nextAnswers);
        setDirty(false);
        setResult(preferred.result || null);
      } else {
        setActiveSessionId(null);
        setAnswers({});
        setDirty(false);
        setResult(null);
        if (!rows.length) setPct(view, 0);
      }
    } catch (e: any) {
      if (gen !== loadGenRef.current) return;
      setError(e?.message || "Failed to load assessment");
    } finally {
      if (gen === loadGenRef.current) setLoading(false);
    }
  }, [
    assessmentId,
    fetchConfigForSession,
    mode,
    patientId,
    setPct,
    view,
  ]);

  useEffect(() => {
    load();
    return () => {
      loadGenRef.current += 1;
    };
  }, [load]);

  const selectSession = useCallback(
    async (sessionId: string) => {
      const row = sessions.find((s) => sessionKey(s) === sessionId);
      if (!row) return;
      const gen = ++selectGenRef.current;
      setActiveSessionId(sessionId);
      setDirty(false);
      setResult(row.result || null);
      const fallbackAnswers = flattenCoachAnswers(
        row.answers || {},
        configRef.current,
      );
      setAnswers(fallbackAnswers);
      try {
        const cfg = await fetchConfigForSession(row);
        if (gen !== selectGenRef.current) return;
        const nextAnswers = flattenCoachAnswers(row.answers || {}, cfg);
        setAnswers(nextAnswers);
        setConfig(cfg);
      } catch (e: any) {
        if (gen !== selectGenRef.current) return;
        setError(e?.message || "Failed to load config for session");
      }
    },
    [fetchConfigForSession, sessions],
  );

  /** Queue patches so overlapping Save clicks keep latest answersRef, not last-response-wins. */
  const persist = useCallback(
    async (nextAnswers?: Record<string, unknown>) => {
      const run = async () => {
        if (!patientId || !activeSessionId || isPublished || !roleCanEdit)
          return false;
        const payload = nextAnswers ?? answersRef.current;
        setSaving(true);
        try {
          const data = await patchCoachSession(activeSessionId, {
            patientId,
            answers: payload,
          });
          setResult(data?.result || null);
          const pct = progressFromConfig(configRef.current, payload).pct;
          setSessions((prev) =>
            prev.map((s) =>
              sessionKey(s) === activeSessionId
                ? {
                    ...s,
                    answers: payload,
                    result: data?.result || s.result,
                    pct,
                    answered:
                      data?.answered ??
                      progressFromConfig(configRef.current, payload).answered,
                    action: data?.action || s.action,
                  }
                : s,
            ),
          );
          setDirty(false);
          setSaveNote("All changes saved");
          const saved = sessions.find((s) => sessionKey(s) === activeSessionId);
          if (isLatestVisit(saved, sessions)) await refreshAll();
          return true;
        } catch (e: any) {
          setSaveNote(e?.message || "Save failed");
          return false;
        } finally {
          setSaving(false);
        }
      };
      const queued = persistTailRef.current.then(run, run);
      persistTailRef.current = queued.then(
        () => true,
        () => true,
      );
      return queued;
    },
    [activeSessionId, isPublished, patientId, refreshAll, roleCanEdit, sessions],
  );

  const setAnswer = useCallback(
    (questionId: string, value: unknown) => {
      if (!canEdit) return;
      setAnswers((prev) => {
        const next = { ...prev, [questionId]: value };
        if (isLatestVisit(activeSession, sessions)) {
          applyProgress(configRef.current, next, activeSession?.action);
        }
        return next;
      });
      setDirty(true);
      setSaveNote("Unsaved changes");
    },
    [activeSession, applyProgress, canEdit, sessions],
  );

  /** Persist if dirty (section leave / footer Save). */
  const saveIfDirty = useCallback(async () => {
    if (!dirtyRef.current) return true;
    return persist();
  }, [persist]);

  const saveNow = useCallback(async () => {
    await persist();
  }, [persist]);

  const ensureSession = useCallback(async () => {
    if (!patientId || !roleCanEdit) return null;
    if (mode === AssessmentMode.Followup) return null;
    if (skipBaseline) {
      setConfirmKind(ConfirmKind.BaselineBlock);
      return null;
    }
    const hasBaseline = sessions.some((s) => sessionCount(s) === 0);
    if (hasBaseline) return activeSessionId;
    if (sessions.length) return null;
    const started = await startCoachSession(patientId, assessmentId);
    await load();
    return started?.sessionId || null;
  }, [
    activeSessionId,
    assessmentId,
    load,
    mode,
    patientId,
    roleCanEdit,
    sessions,
    skipBaseline,
  ]);

  const publish = useCallback(async () => {
    if (!patientId || !activeSessionId || isPublished || !roleCanEdit) return;
    const progress = progressFromConfig(configRef.current, answersRef.current);
    if (
      !progress.requiredTotal ||
      progress.requiredAnswered < progress.requiredTotal
    ) {
      setSaveNote(
        `Answer required questions before publishing (${progress.requiredAnswered}/${progress.requiredTotal})`,
      );
      return;
    }
    // Always save first, then publish (persist owns saving state for the save call)
    const saved = await persist();
    if (!saved) return;
    setSaving(true);
    try {
      await completeCoachSession(activeSessionId, {
        patientId,
        answers: answersRef.current,
      });
      await load();
      await refreshAll();
      setSaveNote("Published");
    } catch (e: any) {
      setSaveNote(e?.message || "Publish failed");
      setError(e?.message || "Publish failed");
    } finally {
      setSaving(false);
    }
  }, [
    activeSessionId,
    isPublished,
    load,
    patientId,
    persist,
    refreshAll,
    roleCanEdit,
  ]);
  // New follow-up only when the latest existing session is published (PRD),
  // or when no follow-up exists yet (existing patient skipping baseline).
  const canFollowUp = useMemo(() => {
    if (mode === AssessmentMode.Baseline || !roleCanEdit) return false;
    const hasFollowUp = sessions.some((s) => {
      const n = sessionCount(s);
      return n != null && n >= 1;
    });
    if (!hasFollowUp) return true;
    const latest = [...sessions].sort((a, b) => {
      const ac = typeof a.assessmentCount === "number" ? a.assessmentCount : -1;
      const bc = typeof b.assessmentCount === "number" ? b.assessmentCount : -1;
      if (ac !== bc) return bc - ac;
      return (
        new Date(b.updatedAt || b.date || 0).getTime() -
        new Date(a.updatedAt || a.date || 0).getTime()
      );
    })[0];
    return (latest?.action || "") === "published";
  }, [mode, roleCanEdit, sessions]);

  const createFollowUp = useCallback(async () => {
    if (!patientId || !canFollowUp) return;
    setSaving(true);
    try {
      await startCoachSession(patientId, assessmentId);
      await load();
      await refreshAll();
    } catch (e: any) {
      setError(e?.message || "Could not create follow-up");
    } finally {
      setSaving(false);
    }
  }, [assessmentId, canFollowUp, load, patientId, refreshAll]);

  const newFollowUp = useCallback(async () => {
    if (!patientId || !canFollowUp) return;
    if (!baselinePublished) {
      setConfirmKind(ConfirmKind.FollowupWarn);
      return;
    }
    await createFollowUp();
  }, [baselinePublished, canFollowUp, createFollowUp, patientId]);

  const confirmFollowUpCreate = useCallback(async () => {
    setConfirmKind(null);
    await createFollowUp();
  }, [createFollowUp]);

  const closeConfirm = useCallback(() => setConfirmKind(null), []);

  const cancelFollowUp = useCallback(
    async (sessionId?: string) => {
      if (!patientId || !roleCanEdit) return;
      const targetId = sessionId || activeSessionId;
      if (!targetId) return;
      const row = sessions.find((s) => sessionKey(s) === targetId);
      if (!row) return;
      const rowAction = row.action || "draft";
      const rowSource = row.source || "patient";
      if (rowAction === "published") return;
      // Menu trash / actionbar: coach drafts (and unpublished follow-ups)
      if (sessionId && rowSource !== "coach" && rowAction !== "draft") return;
      setSaving(true);
      try {
        await cancelCoachSession(targetId, patientId);
        await load();
        await refreshAll();
      } catch (e: any) {
        setError(e?.message || "Could not cancel follow-up");
      } finally {
        setSaving(false);
      }
    },
    [activeSessionId, load, patientId, refreshAll, roleCanEdit, sessions],
  );

  const sectionAnswered = useCallback(
    (sectionId: string) => {
      const qs = (config?.questions || []).filter(
        (q: any) =>
          q.sectionId === sectionId && questionIsVisible(q, answers),
      );
      let a = 0;
      qs.forEach((q: any) => {
        if (isAnswered(answers[q.id])) a += 1;
      });
      return { answered: a, total: qs.length };
    },
    [answers, config],
  );

  const overallProgress = useMemo(
    () => progressFromConfig(config, answers),
    [answers, config],
  );

  const formatSessionDate = useCallback((s: any) => {
    const raw = s?.createdAt || s?.date || s?.updatedAt;
    if (!raw) return "";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }, []);

  const sessionLabel = useCallback((s: any) => {
    if (s?.occurrence) return String(s.occurrence);
    if (s?.label) return String(s.label);
    if (typeof s?.assessmentCount === "number") {
      return s.assessmentCount === 0
        ? "Baseline"
        : `Follow-up ${s.assessmentCount}`;
    }
    return "Session";
  }, []);

  // Lift session chrome into sticky PatientHeader while this page is mounted.
  useEffect(() => {
    if (loading) {
      registerChrome(null);
      return () => registerChrome(null);
    }
    registerChrome({
      sessions: pickerSessions.map((s, idx) => {
        const id = sessionKey(s);
        const sAction = s.action || "draft";
        const sSource = s.source || "patient";
        const count =
          typeof s.assessmentCount === "number" ? s.assessmentCount : idx;
        return {
          sessionId: id,
          label: sessionLabel(s),
          date: formatSessionDate(s),
          action: sAction,
          source: sSource,
          canCancel:
            roleCanEdit &&
            sAction !== "published" &&
            count > 0 &&
            (sSource === "coach" || sAction === "draft"),
        };
      }),
      activeSessionId,
      occurrence,
      activeDate: formatSessionDate(activeSession),
      action,
      source,
      canFollowUp,
      saving,
      mode,
      selectSession,
      newFollowUp,
      cancelFollowUp,
    });
    return () => registerChrome(null);
  }, [
    action,
    activeSession,
    activeSessionId,
    cancelFollowUp,
    canFollowUp,
    formatSessionDate,
    loading,
    mode,
    newFollowUp,
    occurrence,
    pickerSessions,
    registerChrome,
    roleCanEdit,
    saving,
    selectSession,
    sessionLabel,
    source,
  ]);

  return {
    assessmentId,
    config,
    sessions,
    activeSession,
    activeSessionId,
    answers,
    result,
    coachQuestions,
    loading,
    saving,
    saveNote,
    error,
    action,
    isPublished,
    isPreview,
    source,
    canEdit,
    roleCanEdit,
    baselinePublished,
    allSessions: sessions,
    pickerSessions,
    occurrence,
    assessmentCount,
    overallProgress,
    canFollowUp,
    setAnswer,
    selectSession,
    saveNow,
    saveIfDirty,
    ensureSession,
    publish,
    newFollowUp,
    confirmFollowUpCreate,
    closeConfirm,
    confirmKind,
    skipBaseline,
    cancelFollowUp,
    sectionAnswered,
    reload: load,
  };
};
